/**
 * Searchable §11 directory picker — delegate to ANYONE in the company roster (~160 people), the same
 * source the standalone task manager uses. Replaces the old local-staff <select> that only listed the
 * tiny seed roster (all "unmapped"). Shared by the floating/contextual delegate modal and the /tasks form.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTaskDirectory } from '../lib/api';

export interface Assignee { uuid: string; name: string; }

export default function AssigneePicker({ value, onChange, disabled }: {
  value: Assignee | null;
  onChange: (v: Assignee | null) => void;
  disabled?: boolean;
}) {
  const dir = useQuery({ queryKey: ['task-directory'], queryFn: getTaskDirectory, enabled: !disabled });
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);

  if (value) {
    return (
      <div className="row" style={{ gap: 6, alignItems: 'center' }}>
        <span className="pill pill-stage small">👤 {value.name}</span>
        {!disabled && <button type="button" className="small" onClick={() => onChange(null)}>更换 / change</button>}
      </div>
    );
  }

  const all = dir.data ?? [];
  const ql = q.trim().toLowerCase();
  const matches = (ql ? all.filter((u) => (u.username ?? '').toLowerCase().includes(ql) || (u.employee_id ?? '').toLowerCase().includes(ql)) : all).slice(0, 60);
  const open = (focused || !!q) && !disabled;

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={q}
        disabled={disabled}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={dir.isLoading ? '加载目录… / loading…' : dir.isError ? '目录不可用 / directory unavailable' : '🔍 搜索员工姓名 / search employee…'}
      />
      {open && (
        <div className="pop-menu" style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, maxHeight: 220, overflowY: 'auto', background: 'var(--card,#fff)', border: '1px solid var(--rule)', marginTop: 2 }}>
          {matches.length === 0 && <div className="small muted" style={{ padding: 8 }}>无匹配 / no match</div>}
          {matches.map((u) => (
            <button
              type="button"
              key={u.uuid}
              onMouseDown={(e) => { e.preventDefault(); onChange({ uuid: u.uuid, name: u.username || '—' }); setQ(''); setFocused(false); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid var(--rule)', background: 'transparent', padding: '7px 10px', cursor: 'pointer', fontSize: 14 }}
            >
              {u.username}
            </button>
          ))}
          {all.length > matches.length && <div className="small muted" style={{ padding: 6 }}>… 输入以缩小范围 / type to narrow ({all.length} total)</div>}
        </div>
      )}
    </div>
  );
}
